function isObject(item) {
    return (item && typeof item === 'object' && !Array.isArray(item));
}
function mergeDeep(target, source) {
    if (!target) {
        return source;
    }
    if (!source) {
        return target;
    }
    if (Array.isArray(target) && Array.isArray(source)) {
        return [...new Set(target.concat(source))];
    }
    const output = Object.assign({}, target);
    if (isObject(target) && isObject(source)) {
        Object.keys(source)
            .forEach((key) => {
            if ((isObject(source[key]) || Array.isArray(source[key])) && key in target) {
                output[key] = mergeDeep(target[key], source[key]);
            }
            else {
                Object.assign(output, { [key]: source[key] });
            }
        });
    }
    return output;
}

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

function hateoasProcessStrategy(_a = {}) {
    var { embedded = {} } = _a, data = __rest(_a, ["embedded"]);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const entity = __rest(data, ["links"]);
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    const relations = processRelations(embedded, Object.keys(embedded));
    return Object.assign(Object.assign({}, entity), relations);
}
function hateoasWithCustomFieldsProcessStrategy(data = {}, customFields = []) {
    const entity = hateoasProcessStrategy(data);
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    const customRelations = processRelations(entity, customFields);
    return Object.assign(Object.assign({}, entity), customRelations);
}
function processRelations(data, properties) {
    const relations = {};
    properties.forEach((property) => {
        if (!Object.prototype.hasOwnProperty.call(data, property)) {
            return;
        }
        const relation = data[property];
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        relations[property] = processRelation(relation);
    });
    return relations;
}
function processRelation(data) {
    if (data === null) {
        return null;
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const relation = __rest(data, ["links"]);
    if (Array.isArray(relation.items)) {
        // @ts-ignore
        return relation
            .items
            .map((item) => hateoasProcessStrategy(item));
    }
    if (relation.embedded) {
        return hateoasProcessStrategy(relation);
    }
    return relation;
}

const MODULE_ENTITIES = 'entities';
// GETTER
const $ALL_ENTITIES = '$ALL_ENTITIES';
const $GET_ENTITIES_BY_TYPE = '$GET_ENTITIES_BY_TYPE';
const $GET_ENTITY_BY_ID = '$GET_ENTITY_BY_ID';
const $GET_ENTITIES_BY_ID = '$GET_ENTITIES_BY_ID';
const $FILTER_ENTITIES_BY = '$FILTER_ENTITIES_BY';
// MUTATION
const MUTATION_ENTITIES_UPDATE = 'MUTATION_ENTITIES_UPDATE';
const MUTATION_ENTITIES_UPDATE_KEY = 'MUTATION_ENTITIES_UPDATE_KEY';
const MUTATION_ENTITIES_MERGE = 'MUTATION_ENTITIES_MERGE';
const MUTATION_ENTITIES_DELETE_KEY = 'MUTATION_ENTITIES_DELETE_KEY';
const initialState = () => ({});
const DEFAULT_KEY = 'id';
const getters = {
    [$ALL_ENTITIES]: (state) => state,
    [$GET_ENTITIES_BY_TYPE]: (state) => (type, toArray = true) => {
        const entities = state[type] || {};
        if (toArray) {
            return Object.values(entities);
        }
        return entities;
    },
    [$GET_ENTITY_BY_ID]: (state, getters) => (type, value) => {
        if (!value) {
            return null;
        }
        const entities = getters[$GET_ENTITIES_BY_TYPE](type, false);
        return entities[value];
    },
    [$GET_ENTITIES_BY_ID]: (state, getters) => (type, value, isCustomKey = false) => {
        if (!Array.isArray(value)) {
            throw new Error('Getter `$GET_ENTITIES_BY_ID` accepts only array of ids as value.');
        }
        if (isCustomKey) {
            const entitiesObject = getters[$GET_ENTITIES_BY_TYPE](type, false);
            return Object
                .entries(entitiesObject)
                .filter(([id]) => value.includes(id))
                .map(([, entity]) => entity);
        }
        const entities = getters[$GET_ENTITIES_BY_TYPE](type);
        return entities.filter((entity) => value.includes(entity[DEFAULT_KEY]));
    },
    [$FILTER_ENTITIES_BY]: (state, getters) => (type, key, value) => {
        if (!value) {
            return null;
        }
        const entities = getters[$GET_ENTITIES_BY_TYPE](type);
        if (Array.isArray(value)) {
            return entities.filter((entity) => value.includes(entity[key]));
        }
        return entities.filter((en) => en[key] === value);
    },
};
const mutations = {
    [MUTATION_ENTITIES_UPDATE](state, entities) {
        Object
            .keys(entities)
            .forEach((type) => {
            if (!state[type]) {
                state[type] = {};
            }
            Object
                .keys(entities[type])
                .forEach((entity) => {
                const oldObj = state[type][entity] || {};
                const newObj = entities[type][entity] || {};
                state[type][entity] = Object.assign(Object.assign({}, oldObj), newObj);
            });
        });
    },
    [MUTATION_ENTITIES_UPDATE_KEY](state, { entity, key, payload }) {
        state[entity] = Object.assign(Object.assign({}, (state[entity] || {})), { [key]: Object.assign(Object.assign({}, (state[entity] || {})[key]), payload) });
    },
    [MUTATION_ENTITIES_MERGE](state, entities) {
        Object
            .keys(entities)
            .forEach((type) => {
            if (!state[type]) {
                state[type] = {};
            }
            Object
                .keys(entities[type])
                .forEach((entity) => {
                const oldObj = state[type][entity];
                const newObj = entities[type][entity];
                state[type][entity] = mergeDeep(oldObj, newObj);
            });
        });
    },
    [MUTATION_ENTITIES_DELETE_KEY](state, { entity, key }) {
        delete state[entity][key];
    },
};
const entitiesModule = () => ({
    getters,
    mutations,
    state: initialState(),
});
function initEntities() {
    return (store) => {
        store.registerModule(MODULE_ENTITIES, entitiesModule());
    };
}

export { $ALL_ENTITIES, $FILTER_ENTITIES_BY, $GET_ENTITIES_BY_ID, $GET_ENTITIES_BY_TYPE, $GET_ENTITY_BY_ID, MODULE_ENTITIES, MUTATION_ENTITIES_DELETE_KEY, MUTATION_ENTITIES_MERGE, MUTATION_ENTITIES_UPDATE, MUTATION_ENTITIES_UPDATE_KEY, hateoasProcessStrategy, hateoasWithCustomFieldsProcessStrategy, initEntities };
